import { useState, useCallback } from 'react';
import { useGlobalAPI } from '../../../../hooks/useGlobalAPI';

/**
 * Custom hook for Activity Frequency Ratio KPI calculation.
 * 
 * Calculates: Sum(selected activity frequencies) / Total objects of selected object type
 * 
 * @param {string} activeFile - The currently active file
 * @returns {object} KPI data, loading state, error state, and fetch function
 */
export const useActivityFrequencyRatioKPI = (activeFile) => {
  // ✅ Use unified API hook - automatically applies global filters
  const api = useGlobalAPI(activeFile);

  // State for KPI data and UI
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchKPIData = useCallback(async (objectType, activityNames) => {
    if (!activeFile) {
      setError("No active file selected");
      return;
    }

    // Validate inputs
    if (!objectType) {
      setError("Please select an object type");
      return;
    }

    if (!activityNames || activityNames.length === 0) {
      setError("Please select at least one activity");
      return;
    }

    setLoading(true);
    setError(null);
    setHasFetched(true);

    try {
      console.log("🔄 Fetching activity frequency ratio KPI data...", {
        activeFile,
        objectType,
        activityNames,
      });

      const requestPayload = {
        object_type: objectType,
        activity_names: activityNames,
      };
      const response = await api.post('/kpi/activity-frequency-ratio', requestPayload, {}, 'Activity Frequency Ratio KPI');
      
      console.log("📥 Activity Frequency Ratio KPI response received:", response);
      setData(response);
    } catch (err) {
      console.error("❌ Error during Activity Frequency Ratio KPI fetch:", err);
      setError(err.message || "An unknown error occurred.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activeFile, api]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setHasFetched(false);
    setLoading(false);
  }, []);

  return {
    data,
    loading,
    error,
    hasFetched,
    fetchKPIData,
    reset
  };
};